import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Play, Pause, Webhook as WebhookIcon, Activity, FileText, Lock, Loader2, Inbox, RefreshCw, RotateCw, AlertTriangle, Pencil, Trash2, ChevronRight, Copy, Check } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { CreateWebhookDialog } from "@/components/webhooks/CreateWebhookDialog";
import { EditWebhookDialog } from "@/components/webhooks/EditWebhookDialog";
import { SecretRevealDialog } from "@/components/webhooks/SecretRevealDialog";

type WebhookSubscriptionResponse = components["schemas"]["WebhookSubscriptionResponse"];
type WebhookDeliveryResponse = components["schemas"]["WebhookDeliveryResponse"];

const dateTimeFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

export function WebhooksPage() {
  const { activeClientId } = useAuth();
  const [subs, setSubs] = useState<WebhookSubscriptionResponse[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [revealTitle, setRevealTitle] = useState("");
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/v1/webhook-subscriptions")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setError("Couldn't load webhook subscriptions.");
          return;
        }
        const list = data ?? [];
        setSubs(list);
        setSelectedId((prev) => {
          if (prev && list.some((s) => s.id === prev)) return prev;
          return list[0]?.id ?? null;
        });
      })
      .catch(() => { if (!cancelled) setError("Network error fetching webhooks."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeClientId, tick]);

  const selected = useMemo(
    () => subs?.find((s) => s.id === selectedId) ?? null,
    [subs, selectedId]
  );

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-6 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Webhooks</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Subscribe to bill and invoice lifecycle events.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} disabled={!activeClientId}>
          <Plus className="size-3.5" /> New webhook
        </Button>
      </div>

      <CreateWebhookDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(created) => {
          refresh();
          if (created.id) setSelectedId(created.id);
          setRevealTitle("Signing secret for new subscription");
          setRevealedSecret(created.secret);
        }}
      />

      <SecretRevealDialog
        open={revealedSecret !== null}
        onOpenChange={(o) => { if (!o) setRevealedSecret(null); }}
        secret={revealedSecret}
        title={revealTitle}
        description="Use this secret to verify the Dolfin-Signature header on each delivery."
      />

      {error ? (
        <ErrorState message={error} />
      ) : loading ? (
        <LoadingState />
      ) : !subs || subs.length === 0 ? (
        <EmptyState onNew={() => setCreateOpen(true)} canCreate={!!activeClientId} />
      ) : (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-5">
            <Card className="overflow-hidden p-0 gap-0">
              <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <span>Endpoints · {subs.length}</span>
                <button onClick={refresh} className="hover:text-foreground" title="Refresh">
                  <RefreshCw className="size-3" />
                </button>
              </div>
              <div className="flex flex-col">
                {subs.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedId(s.id ?? null)}
                    className={cn(
                      "flex flex-col gap-1 border-b border-border px-3 py-2.5 text-left transition last:border-b-0",
                      s.id === selectedId ? "bg-secondary/15" : "hover:bg-accent"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <WebhookIcon className="size-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate font-mono text-xs">{s.url}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.active
                        ? <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px]">Active</Badge>
                        : <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400 text-[10px]">Disabled</Badge>}
                      <span className="text-[11px] text-muted-foreground">
                        {s.eventTypes.length} event{s.eventTypes.length === 1 ? "" : "s"}
                        {s.consecutiveFailures != null && Number(s.consecutiveFailures) > 0
                          ? ` · ${s.consecutiveFailures} consecutive failures`
                          : ""}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          <div className="col-span-7">
            {selected ? (
              <WebhookDetail
                sub={selected}
                onChanged={refresh}
                onDeleted={() => {
                  setSelectedId(null);
                  refresh();
                }}
                onSecretRotated={(secret) => {
                  setRevealTitle("Rotated signing secret");
                  setRevealedSecret(secret);
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Select a webhook</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function WebhookDetail({
  sub,
  onChanged,
  onDeleted,
  onSecretRotated,
}: {
  sub: WebhookSubscriptionResponse;
  onChanged: () => void;
  onDeleted: () => void;
  onSecretRotated: (secret: string) => void;
}) {
  const [busy, setBusy] = useState<"toggle" | "rotate" | "delete" | null>(null);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  async function toggleActive() {
    if (!sub.id) return;
    setBusy("toggle");
    const { error } = await api.PATCH("/v1/webhook-subscriptions/{id}", {
      params: { path: { id: sub.id } },
      body: { active: !sub.active },
    });
    setBusy(null);
    if (!error) onChanged();
  }

  async function rotate() {
    if (!sub.id) return;
    setBusy("rotate");
    const { data, error } = await api.POST("/v1/webhook-subscriptions/{id}/rotate-secret", {
      params: { path: { id: sub.id } },
    });
    setBusy(null);
    setRotateConfirmOpen(false);
    if (!error && data?.secret) {
      onSecretRotated(data.secret);
      onChanged();
    }
  }

  async function deleteSubscription() {
    if (!sub.id) return;
    setBusy("delete");
    const { error } = await api.DELETE("/v1/webhook-subscriptions/{id}", {
      params: { path: { id: sub.id } },
    });
    setBusy(null);
    setDeleteConfirmOpen(false);
    if (!error) onDeleted();
  }

  return (
    <Card className="p-0 gap-0 overflow-hidden">
      <EditWebhookDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        subscription={sub}
        onSaved={onChanged}
      />
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="font-mono text-sm truncate">{sub.url}</div>
          <div className="mt-1 text-xs text-muted-foreground">ID {sub.id}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} disabled={busy !== null}>
            <Pencil className="size-3.5" /> Edit
          </Button>
          {sub.active
            ? <Button variant="outline" size="sm" onClick={toggleActive} disabled={busy === "toggle"}><Pause className="size-3.5" /> Pause</Button>
            : <Button variant="outline" size="sm" onClick={toggleActive} disabled={busy === "toggle"}><Play  className="size-3.5" /> Resume</Button>}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={busy !== null}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="size-3.5" /> Delete
          </Button>
        </div>
      </div>

      <Tabs defaultValue="config" className="w-full">
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent px-2">
          <TabsTrigger value="config"><FileText className="size-3.5" /> Config</TabsTrigger>
          <TabsTrigger value="deliveries"><Activity className="size-3.5" /> Deliveries</TabsTrigger>
          <TabsTrigger value="secrets"><Lock className="size-3.5" /> Secrets</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="px-4 py-4">
          <div className="flex flex-col gap-4 text-sm">
            <Field label="Endpoint URL" mono>{sub.url}</Field>
            <Field label="Subscribed events">
              <div className="flex flex-wrap gap-1.5">
                {sub.eventTypes.length === 0 && <span className="text-muted-foreground">— no events —</span>}
                {sub.eventTypes.map((e, idx) => (
                  <Badge key={idx} variant="outline" className="font-mono">{String(e)}</Badge>
                ))}
              </div>
            </Field>
            <Field label="Health">
              <span className="tabular-nums">
                {sub.consecutiveFailures != null ? sub.consecutiveFailures : 0} consecutive failures
              </span>
            </Field>
            <Field label="Created">
              <span>{sub.createdAt ? dateTimeFmt.format(new Date(sub.createdAt)) : "—"}</span>
            </Field>
            <Field label="Updated">
              <span>{sub.updatedAt ? dateTimeFmt.format(new Date(sub.updatedAt)) : "—"}</span>
            </Field>
          </div>
        </TabsContent>

        <TabsContent value="deliveries" className="px-4 py-4">
          <DeliveriesTab sub={sub} />
        </TabsContent>

        <TabsContent value="secrets" className="px-4 py-4">
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <div className="font-semibold">Signing secret</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Stored hashed; revealed once at creation. Rotating produces a new secret and invalidates
              the previous one immediately. Existing in-flight deliveries continue to use the old secret.
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => setRotateConfirmOpen(true)}
            >
              <RotateCw className="size-3.5" /> Rotate secret
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this webhook?</DialogTitle>
            <DialogDescription>
              The endpoint will stop receiving events immediately. In-flight retries are
              cancelled. Past delivery history is removed too. This is irreversible.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <span className="break-all"><span className="font-mono">{sub.url}</span></span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={deleteSubscription} disabled={busy === "delete"} className="bg-destructive text-white hover:opacity-90">
              <Trash2 className="size-3.5" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rotateConfirmOpen} onOpenChange={setRotateConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rotate signing secret?</DialogTitle>
            <DialogDescription>
              The old secret stops working immediately. Update your endpoint to verify with the new
              secret as soon as you receive it.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span>This is irreversible. The new secret will be shown once.</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRotateConfirmOpen(false)}>Cancel</Button>
            <Button onClick={rotate} disabled={busy === "rotate"}>
              <RotateCw className="size-3.5" /> Rotate now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DeliveriesTab({ sub }: { sub: WebhookSubscriptionResponse }) {
  const [deliveries, setDeliveries] = useState<WebhookDeliveryResponse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!sub.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/v1/webhook-subscriptions/{id}/deliveries", { params: { path: { id: sub.id } } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setError("Couldn't load deliveries."); return; }
        setDeliveries(data ?? []);
      })
      .catch(() => { if (!cancelled) setError("Network error fetching deliveries."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sub.id, tick]);

  async function retry(deliveryId: string) {
    if (!sub.id) return;
    const { error } = await api.POST(
      "/v1/webhook-subscriptions/{id}/deliveries/{deliveryId}/retry",
      { params: { path: { id: sub.id, deliveryId } } }
    );
    if (!error) refresh();
  }

  if (loading) {
    return <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Loading deliveries…</div>;
  }
  if (error) {
    return <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>;
  }
  if (!deliveries || deliveries.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
        <Inbox className="size-6" />
        No deliveries yet. As events fire, signed POSTs to this endpoint will appear here.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <span>{deliveries.length} deliver{deliveries.length === 1 ? "y" : "ies"}</span>
        <button onClick={refresh} className="flex items-center gap-1 hover:text-foreground"><RefreshCw className="size-3" /> Refresh</button>
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead className="w-6"></TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Organisation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">HTTP</TableHead>
              <TableHead className="text-right">Attempts</TableHead>
              <TableHead>Last attempt</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deliveries.map((d) => (
              <DeliveryRow key={d.id} delivery={d} onRetry={retry} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function DeliveryRow({
  delivery, onRetry,
}: { delivery: WebhookDeliveryResponse; onRetry: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const isDelivered = delivery.status?.toLowerCase() === "delivered";

  const prettyPayload = useMemo(() => {
    if (!delivery.payload) return "";
    try { return JSON.stringify(JSON.parse(delivery.payload), null, 2); }
    catch { return delivery.payload; }
  }, [delivery.payload]);

  // Organisation OR company, never both (an unassigned bill has no organisation). The all-zeros guard is for rows
  // predating the column.
  const tenant = useMemo(() => {
    const org = delivery.organisationId;
    if (org && org !== "00000000-0000-0000-0000-000000000000") return { label: "Org", id: org };
    if (delivery.companyId) return { label: "Company", id: delivery.companyId };
    return null;
  }, [delivery.organisationId, delivery.companyId]);

  async function copyPayload() {
    if (!delivery.payload) return;
    await navigator.clipboard.writeText(prettyPayload);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <>
      <TableRow
        onClick={() => setExpanded((v) => !v)}
        className="cursor-pointer hover:bg-accent/40"
      >
        <TableCell className="w-6 pr-0 text-muted-foreground">
          <ChevronRight className={cn("size-3.5 transition-transform", expanded && "rotate-90")} />
        </TableCell>
        <TableCell><span className="font-mono text-xs">{delivery.eventType}</span></TableCell>
        <TableCell>
          <span className="font-mono text-[11px] text-muted-foreground">
            {tenant ? tenant.id.slice(0, 8) + "…" : "—"}
          </span>
        </TableCell>
        <TableCell><DeliveryStatus status={delivery.status} /></TableCell>
        <TableCell className="text-right tabular-nums">{delivery.lastStatusCode ?? "—"}</TableCell>
        <TableCell className="text-right tabular-nums">{delivery.attempts ?? 0}</TableCell>
        <TableCell className="text-muted-foreground text-xs">
          {delivery.lastAttemptAt ? dateTimeFmt.format(new Date(delivery.lastAttemptAt)) : "—"}
        </TableCell>
        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
          {!isDelivered && delivery.id && (
            <Button variant="ghost" size="xs" onClick={() => onRetry(delivery.id!)}>
              <RotateCw className="size-3" /> Retry
            </Button>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={8} className="px-3 py-3">
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <span><span className="uppercase tracking-wider">Event ID</span>{" "}<span className="font-mono">{delivery.eventId}</span></span>
                <span><span className="uppercase tracking-wider">Delivery ID</span>{" "}<span className="font-mono">{delivery.id}</span></span>
                {tenant && (
                  <span>
                    <span className="uppercase tracking-wider">{tenant.label}</span>{" "}
                    <span className="font-mono">{tenant.id}</span>
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Raw payload
                </span>
                <Button variant="ghost" size="xs" onClick={copyPayload}>
                  {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <pre className="max-h-80 overflow-auto rounded-md border border-border bg-background p-3 text-[11px] font-mono leading-relaxed">
                {prettyPayload || "—"}
              </pre>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function DeliveryStatus({ status }: { status: string }) {
  const s = status?.toLowerCase() ?? "";
  if (s === "delivered")
    return <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">Delivered</Badge>;
  if (s === "failed")
    return <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400">Failed</Badge>;
  return <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400">{status}</Badge>;
}

function Field({ label, mono, children }: { label: string; mono?: boolean; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? "font-mono text-sm break-all" : "text-sm"}>{children}</div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Loading webhook subscriptions…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
      {message}
    </div>
  );
}

function EmptyState({ onNew, canCreate }: { onNew: () => void; canCreate: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border py-16 text-center">
      <Inbox className="size-7 text-muted-foreground" />
      <p className="text-sm font-medium">No webhook subscriptions yet.</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        Create one to receive bill and invoice events. Signing secret is shown once at creation.
      </p>
      <Button size="sm" onClick={onNew} disabled={!canCreate}>
        <Plus className="size-3.5" /> New webhook
      </Button>
    </div>
  );
}
